"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import useIdleTimeout from "@/app/components/hooks/useIdleTimeout";
import {
  IDLE_TIMEOUT_MINUTES,
  IDLE_WARNING_MINUTES,
} from "@/app/lib/util/session.config";
import { toast, Bounce } from "@/app/lib/util/toast.client";

/**
 * Component to handle automatic logout after idle timeout
 * Only active when user is on protected routes (logged in)
 */
const IdleTimeoutHandler: React.FC = () => {
  const currentURL = usePathname();

  // Determine if we're on a protected route (user is logged in)
  const isProtectedRoute =
    currentURL !== null &&
    currentURL !== undefined &&
    !currentURL.includes("/login") &&
    !currentURL.includes("/register") &&
    currentURL !== "/";

  const formatIdleWarningTime = (minutes: number): string => {
    if (minutes >= 24 * 60) {
      const days = Math.round(minutes / (24 * 60));
      return `${days} day${days > 1 ? "s" : ""}`;
    }
    if (minutes >= 60) {
      const hours = Math.round(minutes / 60);
      return `${hours} hour${hours > 1 ? "s" : ""}`;
    }
    return `${minutes} minute${minutes > 1 ? "s" : ""}`;
  };

  // Show warning toast before logout
  const handleWarning = (remainingMinutes: number) => {
    toast.warning(
      `🦄 Your session will expire in ${formatIdleWarningTime(remainingMinutes)}. Please interact with the page to stay logged in.`,
      {
        position: "top-right",
        autoClose: false, // Keep warning visible
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
        pauseOnFocusLoss: false,
        toastId: "idle-warning", // Use unique ID to prevent duplicates
      },
    );
  };

  // Handle logout
  const handleLogout = () => {
    toast.info("🦄 You have been logged out due to inactivity.", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: true,
      theme: "light",
      transition: Bounce,
      pauseOnFocusLoss: false,
      toastId: "idle-logout",
    });
  };

  // Initialize idle timeout (only when on protected routes)
  useIdleTimeout({
    timeoutMinutes: IDLE_TIMEOUT_MINUTES,
    warningMinutes: IDLE_WARNING_MINUTES,
    onWarning: handleWarning,
    onLogout: handleLogout,
    enabled: isProtectedRoute, // Only enable when user is logged in
  });

  return null; // This component doesn't render anything
};

export default IdleTimeoutHandler;
